/**
 * `useRouterState` selectors for the `/trades` route's search params, used by
 * the saved-view bootstrap (LR-671). Return primitives so `useRouterState`'s
 * equality bailout keeps the value stable across re-renders — same convention
 * as `features/inventory/router-helpers`.
 */
type RouterSearchState = { location: { search: unknown } };

/** The `/trades` route's search params — must stay in lock-step with the
 *  `validateSearch` return type in router.tsx. */
export interface TradesSearch {
  counterparty?: number | string;
  view_id?: number;
  mode: 'mtm' | 'history' | undefined;
  cp: number | undefined;
  htab?: 'mtm' | 'trades' | 'cashpool';
  hview?: 'date' | 'cp';
  sel?: string;
}

/**
 * Produce a full TradesSearch from the previous search plus a patch — the
 * `navigate({ search: (prev) => … })` reducer must return exactly this route's
 * shape, and `prev` there is typed as the router-wide union of every route's
 * params (so a plain spread does not type-check). Each field is narrowed at
 * runtime for the same reason (`cp` is a string on /inventory).
 */
export function mergeTradesSearch(
  prev: Record<string, unknown> | undefined,
  patch: Partial<TradesSearch>,
): TradesSearch {
  const p = prev ?? {};
  return {
    counterparty:
      typeof p.counterparty === 'number' || typeof p.counterparty === 'string'
        ? p.counterparty
        : undefined,
    view_id: typeof p.view_id === 'number' ? p.view_id : undefined,
    mode: p.mode === 'mtm' || p.mode === 'history' ? p.mode : undefined,
    cp: typeof p.cp === 'number' ? p.cp : undefined,
    htab: p.htab === 'mtm' || p.htab === 'trades' || p.htab === 'cashpool' ? p.htab : undefined,
    hview: p.hview === 'date' || p.hview === 'cp' ? p.hview : undefined,
    sel: typeof p.sel === 'string' ? p.sel : undefined,
    ...patch,
  };
}

/** The Trades LIST route only — never the single-trade page `/trades/$tradeId`
 *  (LR-208: a `startsWith('/trades')` guard clobbered in-flight trade opens). */
export const isTradesPath = (pathname: string) =>
  pathname === '/trades' || pathname === '/trades/';

export const selectTradesViewId = (s: RouterSearchState): number | undefined => {
  // /trades validateSearch coerces view_id to number | undefined. Require a
  // TRUTHY number to mirror validateSearch's own `view_id ? … : undefined`: a
  // hand-edited `?view_id=0` / `?view_id=abc` yields 0 / NaN, which must fall
  // back to "no view" (the default-view branch), not be loaded as an id. NaN in
  // particular must not leak — it breaks useRouterState's `===` bailout.
  const raw = (s.location.search as Record<string, unknown>)?.view_id;
  return typeof raw === 'number' && raw ? raw : undefined;
};

/**
 * True on the History → Mark to Market / Cash Pool sub-tabs: self-contained
 * logs that render neither the trades table nor the filter sidebar, so saved
 * views don't apply there (mirror of Inventory skipping its availability /
 * counterparty-reports tabs).
 */
export const selectTradesHistoryLog = (s: RouterSearchState): boolean => {
  const search = s.location.search as Record<string, unknown> | undefined;
  return search?.mode === 'history' && (search?.htab === 'mtm' || search?.htab === 'cashpool');
};
